import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../apis/store";
import { useCallback, useEffect, useMemo, useState } from "react";
import PaginationComponent from "../../../Components/biblio/PaginationComponent";
import MyOrdersDetails from "./MyOrdersDetails";
import { useTranslation } from "react-i18next";
import { IoIosArrowBack, IoIosArrowForward } from "react-icons/io";
import { HiOutlineCalendarDateRange } from "react-icons/hi2";
import { MdPayments } from "react-icons/md";
import "moment/locale/ar";
import ProfileTabState from "./ProfileTabState";
import { toGetMyAppointments } from "../../../apis/actions/appointments.actions";
import {
  formatOrderDateTime,
  orderStateLabel,
  paymentMethodLabel,
} from "../../../utils/orderDisplay";

export const MyOrders = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const itemPerPage = 4;
  const [currentPage, setActive] = useState<number>(1);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [selectedData, setSelectedData] = useState<any>(null);

  const language = i18n.language;
  const isRtl = language.startsWith("ar");
  const titleFont = isRtl ? " ac--font-expo" : "";

  const handleOpenModal = useCallback((data: any) => {
    setSelectedData(data);
    setShowModal(true);
  }, []);

  const { myAppointments } = useSelector(
    (state: RootState) => state?.appointmens
  );

  const status = myAppointments?.status ?? "idle";
  const list = myAppointments?.list ?? [];

  useEffect(() => {
    if (status !== "idle") return;
    dispatch(toGetMyAppointments(i18n.language));
  }, [dispatch, status, i18n.language]);

  const loading = status === "idle" || status === "loading";
  const empty =
    (status === "succedded" || status === "rejected") && list.length === 0;

  const pageItems = useMemo(
    () =>
      list.slice(
        itemPerPage * (currentPage - 1),
        itemPerPage * currentPage
      ),
    [list, currentPage]
  );

  const DetailArrow = isRtl ? IoIosArrowBack : IoIosArrowForward;

  return (
    <>
      <ProfileTabState loading={loading} empty={empty} emptyKey="orders">
        <div className="ac-order-list">
          {pageItems.map((el: any) => {
            const isDone = el.order_state === "done";
            const statusLabel = orderStateLabel(el.order_state, t);
            const transactions = el?.transaction_ids ?? [];

            return (
              <article
                key={el.id ?? el.name}
                className="ac-order-card"
              >
                <div className="ac-order-card__main">
                  <header className="ac-order-card__head">
                    <div className="ac-order-card__identity">
                      <p
                        className={`ac-order-card__eyebrow${titleFont}`}
                      >
                        {t("profile.order.orderNumber")}
                      </p>
                      <h3
                        className={`ac-order-card__ref${titleFont}`}
                      >
                        {el.name}
                      </h3>
                    </div>
                  </header>

                  <dl className="ac-order-card__meta">
                    <div className="ac-order-card__meta-row">
                      <dt>
                        <HiOutlineCalendarDateRange
                          className="ac-order-card__meta-icon"
                          aria-hidden
                        />
                        <span className={titleFont}>
                          {t("profile.order.date")}
                        </span>
                      </dt>
                      <dd className={titleFont}>
                        {formatOrderDateTime(el.date_order, language)}
                      </dd>
                    </div>
                    <div className="ac-order-card__meta-row">
                      <dt>
                        <MdPayments
                          className="ac-order-card__meta-icon"
                          aria-hidden
                        />
                        <span className={titleFont}>
                          {t("profile.order.cart.paymentThrough")}
                        </span>
                      </dt>
                      <dd className={titleFont}>
                        {transactions.length === 0 ? (
                          <span className="ac-order-card__meta-muted">—</span>
                        ) : (
                          <ul className="ac-order-card__payments">
                            {transactions.map(
                              (item: any, index: number) => {
                                const methodLabel = paymentMethodLabel(
                                  item?.payment_method_id,
                                  t
                                );

                                return (
                                  <li
                                    key={`${item?.payment_method_id}-${index}`}
                                  >
                                    <span className="ac-order-card__amount icon-saudi_riyal">
                                      {item?.amount}
                                    </span>
                                    <span className="ac-order-card__pay-method">
                                      {methodLabel}
                                    </span>
                                  </li>
                                );
                              }
                            )}
                          </ul>
                        )}
                      </dd>
                    </div>
                  </dl>
                </div>

                <div className="ac-order-card__aside">
                  <span
                    className={`ac-order-card__status ac-order-card__status--${
                      isDone ? "done" : "progress"
                    }${titleFont}`}
                  >
                    {statusLabel}
                  </span>
                  <button
                    type="button"
                    className={`ac-order-card__detail-btn${titleFont}`}
                    onClick={() => handleOpenModal(el)}
                  >
                    {t("profile.order.details")}
                    <DetailArrow aria-hidden />
                  </button>
                </div>
              </article>
            );
          })}
        </div>

        {list.length > itemPerPage && (
          <div className="ac-order-list__pagination">
            <PaginationComponent
              total={list.length}
              itemPerPage={itemPerPage}
              active={currentPage}
              setActive={(nb) => {
                setActive(nb);
              }}
            />
          </div>
        )}
      </ProfileTabState>

      <MyOrdersDetails
        showModal={showModal}
        setShowModal={setShowModal}
        selectedData={selectedData}
      />
    </>
  );
};
export default MyOrders;
